import { EntityScope } from '../../../../shared/utils/enums';
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { Tab, Tabs } from 'react-bootstrap';
import PrimaryButton from '../PrimaryButton';
import i18next from 'i18next';
import { ExerciseCategoryDto } from '../../../../shared/dtos/exerciseCategory.dto';
import ExerciseCategoriesTable from './ExerciseCategoriesTable';
import ExerciseCategoryModal from './ExerciseCategoryModal';
import { ManagementMode, ScopeKey } from '../../util/types';


type ExerciseCategoryManagementProps = {
  mode: ManagementMode;
}

function isScopeTabKey(value: string | null): value is ScopeKey {
  return value === 'tenant' || value === 'core';
}

function getCategoryEndPoint(mode: ManagementMode): string {
  if (mode === 'superAdmin') {
    return '/api/v1/exercise/category/core';
  }

  return '/api/v1/exercise/category';
}

function getModalEntityScope(mode: ManagementMode): EntityScope {
  if (mode === 'superAdmin') {
    return EntityScope.CORE;
  }

  return EntityScope.TENANT;
}

const ExerciseCategoryManagement: FC<ExerciseCategoryManagementProps> = ({ mode }: ExerciseCategoryManagementProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [categories, setCategories] = useState<ExerciseCategoryDto[]>([]);
  const [activeKey, setActiveKey] = useState<ScopeKey>('tenant');

  const [isLoading, setIsLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editCategory, setCategory] = useState<ExerciseCategoryDto | null>(null);

  const categoriesEndpoint: string = getCategoryEndPoint(mode);
  const modalEntityScope: EntityScope = getModalEntityScope(mode);
  const isAdminMode: boolean = mode === 'admin';

  const { tenantCategories, coreCategories } = useMemo(() => {
    const tenant: ExerciseCategoryDto[] = categories.filter((category: ExerciseCategoryDto) => category.scope === EntityScope.TENANT);
    const core: ExerciseCategoryDto[] = categories.filter((category: ExerciseCategoryDto) => category.scope === EntityScope.CORE);

    return { tenantCategories: tenant, coreCategories: core };
  }, [categories]);
  

  const fetchCategories: () => Promise<void> = useCallback(async () => {
    setIsLoading(true);

    try {
      const result: ExerciseCategoryDto[] = await authFetch(categoriesEndpoint, { method: 'GET' });
      setCategories(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [authFetch, categoriesEndpoint]);

  useEffect(() => {
    void fetchCategories();
  }, [fetchCategories]);
  

  const closeModal = (): void => {
    setShowModal(false);
    setCategory(null);
  }

  const openCreateModal = (): void => {
    setCategory(null);
    setShowModal(true);
  }

  const openEditModal = (category: ExerciseCategoryDto): void => {
    setCategory(category);
    setShowModal(true);
  }

  const renderCreateButton = (): ReactElement => {
    return (
      <PrimaryButton
        className="ms-auto"
        onClick={openCreateModal}
        disabled={isLoading}>
        {i18next.t('generic.create')}
      </PrimaryButton>
    )
  }

  const renderTable = (categories: ExerciseCategoryDto[], canEdit: boolean, showCreateButton: boolean): ReactElement => {
    return (
      <ExerciseCategoriesTable
        categories={categories}
        isLoading={isLoading}
        toolbar={showCreateButton ? renderCreateButton() : undefined}
        onClick={canEdit ? openEditModal : undefined}
      />
    )
  }

  const renderAdminContent = (): ReactElement => {
    return (
      <Tabs
        activeKey={activeKey}
        onSelect={(key) => {
          if (isScopeTabKey(key)) {
            setActiveKey(key);
          }
        }}
        className="mb-3 pt-3"
        justify
        mountOnEnter
        unmountOnExit>
        <Tab
          eventKey="tenant"
          title={`${i18next.t('adminExerciseCategories.tenantCategories')} (${tenantCategories.length})`}>
          {renderTable(tenantCategories, true, true)}
        </Tab>
        <Tab
          eventKey="core"
          title={`${i18next.t('adminExerciseCategories.coreCategories')} (${coreCategories.length})`}>
          {renderTable(coreCategories, false, false)}
        </Tab>
      </Tabs>
    )
  }

  const renderSuperAdminContent = (): ReactElement => {
    return (
      <div className="pt-3">
        {renderTable(categories, true, true)}
      </div>
    )
  }

  return (
    <>
      <ExerciseCategoryModal
        show={showModal}
        entityScope={modalEntityScope}
        exerciseCategory={editCategory ?? undefined}
        onClose={closeModal}
        onSave={() => fetchCategories()}
      />

      {isAdminMode ? renderAdminContent() : renderSuperAdminContent()}
    </>
  )
}

export default ExerciseCategoryManagement;
